import {groupBy, groupByUniqueKey, mapObject, partition} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import produce, {Draft} from 'immer';
import React, {useMemo, useState} from 'react';

import {assert} from 'Asserts';
import {
  assertIsAccessible,
  getAccessibleOrInaccessibleId,
  isInaccessible,
} from 'graphql-client/InaccessibleInfo';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import {
  ProcedureTestOrderConfig,
  SourceItemWithTestOrderInfos,
  validateSourceTestOrders,
  validateTestOrder,
} from 'samples/plate-maps/integrations/procedures/PlateMapTestOrderUtils';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipList, {getChipsRowHeight} from 'ui-library/chips/ChipList';
import {GRAY_DARKER} from 'ui-library/Colors';
import Modal from 'ui-library/modals/Modal';
import {DEFAULT_TABLE_COLUMN_WIDTH} from 'ui-library/scrollable/ScrollableTable';
import {
  RowSelectionMode,
  ScrollableTableColumn,
  ScrollableTableRow,
  TABLE_ROW_BODY_HEIGHT,
} from 'ui-library/scrollable/ScrollableTableConstants';
import SortableScrollableTable from 'ui-library/scrollable/SortableScrollableTable';

import {FillPlateTestOrderFragment} from './__generated/FillPlateTestOrderFragment';

interface SourceDilution {
  sourceId: string;
  dilutionFactor: number | null;
}

export const SOURCE_ITEM_COLUMN: ScrollableTableColumn & {accessorKey: 'sourceItemCell'} = {
  header: 'Source',
  accessorKey: 'sourceItemCell',
  flexGrow: 0,
  isFixed: true,
  width: DEFAULT_TABLE_COLUMN_WIDTH,
};
export const CONTENTS_COLUMN: ScrollableTableColumn & {accessorKey: 'contentsCell'} = {
  header: 'Contents',
  accessorKey: 'contentsCell',
  flexGrow: 0,
  width: DEFAULT_TABLE_COLUMN_WIDTH,
};
export const TEST_DILUTION_FACTOR_COLUMN: ScrollableTableColumn & {accessorKey: 'testDilutionFactorCell'} = {
  header: 'Test dilution factor',
  accessorKey: 'testDilutionFactorCell',
  flexGrow: 0,
  width: 200,
  isSortable: true,
  sortKey: 'testDilutionFactor',
};
export const TEST_DEFINITION_NAMES_COLUMN: ScrollableTableColumn & {accessorKey: 'testDefinitionNamesCell'} =
  {
    header: 'Test definitions',
    accessorKey: 'testDefinitionNamesCell',
    flexGrow: 1,
    width: 500,
    isSortable: true,
    sortKey: 'testDefinitionNames',
  };

interface ChooseTestDilutionsForSourcesRow extends ScrollableTableRow {
  data: SourceDilution & {testDefinitionIds: Array<string>} & Record<
      (
        | typeof SOURCE_ITEM_COLUMN
        | typeof CONTENTS_COLUMN
        | typeof TEST_DILUTION_FACTOR_COLUMN
        | typeof TEST_DEFINITION_NAMES_COLUMN
      )['accessorKey'],
      JSX.Element
    >;
}

export function getTestOrders(item: SourceItemWithTestOrderInfos): Array<FillPlateTestOrderFragment> {
  if (item.__typename === 'ContainerNode') {
    // There should only be one content with test orders in the source container
    const sampleContent = item.contents.find(
      (content) => !isInaccessible(content.containedItem) && content.containedItem.testOrders.length > 0
    );
    return sampleContent == null ? [] : assertIsAccessible(sampleContent.containedItem).testOrders;
  } else {
    return item.testOrders;
  }
}

// Mutates the item in place! For use in produce() calls.
function mutateItemToReplaceTestOrders(
  item: Draft<SourceItemWithTestOrderInfos>,
  testOrders: Array<FillPlateTestOrderFragment>
): void {
  if (item.__typename === 'ContainerNode') {
    const sampleContent = item.contents.find(
      (content) => !isInaccessible(content.containedItem) && content.containedItem.testOrders.length > 0
    );
    if (sampleContent != null) {
      assertIsAccessible(sampleContent.containedItem).testOrders = testOrders;
    }
  } else {
    item.testOrders = testOrders;
  }
}

export function getSourceDilutionKey(sd: SourceDilution): string {
  // Item ids are api ids so they will never contain the substring ':::'
  return `${sd.sourceId}:::${sd.dilutionFactor ?? 'no-dilution'}`;
}

function parseSourceDilutionFromKey(key: string): SourceDilution {
  const [sourceId, dilutionFactorString] = key.split(':::');
  return {
    sourceId,
    dilutionFactor: dilutionFactorString === 'no-dilution' ? null : parseInt(dilutionFactorString, 10),
  };
}

function getTestDefinitionIds(testOrders: Array<FillPlateTestOrderFragment>): Array<string> {
  return [
    ...new Set(
      testOrders.map(({testDefinitionCorrect}) => getAccessibleOrInaccessibleId(testDefinitionCorrect))
    ),
  ].sort(
    (a, b) => a.localeCompare(b) // Guarantee stable order so we can easily group rows with same tests
  );
}

function makeTableRow(
  sourceDilutionKey: string,
  selectedSourceDilutionKeys: Set<string>,
  itemsById: Record<string, SourceItemWithTestOrderInfos>
): ChooseTestDilutionsForSourcesRow {
  const {sourceId, dilutionFactor} = parseSourceDilutionFromKey(sourceDilutionKey);
  const item = itemsById[sourceId];
  const contentIds =
    item.__typename === 'ContainerNode'
      ? item.contents.map((c) => getAccessibleOrInaccessibleId(c.containedItem))
      : [];
  const testOrders = getTestOrders(item).filter((to) => to.dilutionFactor === dilutionFactor);
  const testDefinitionIds = getTestDefinitionIds(testOrders);
  return {
    id: sourceDilutionKey,
    isSelected: selectedSourceDilutionKeys.has(sourceDilutionKey),
    data: {
      sourceId,
      dilutionFactor,
      testDefinitionIds,
      [SOURCE_ITEM_COLUMN.accessorKey]: <BenchChip id={sourceId} size={ChipSize.TABLE_CELL} isLink={false} />,
      [CONTENTS_COLUMN.accessorKey]: (
        <ChipList itemIds={contentIds} chipSize={ChipSize.TABLE_CELL} showChipsAsLinks={false} maxChips={2} />
      ),
      [TEST_DILUTION_FACTOR_COLUMN.accessorKey]: <>{dilutionFactor ?? 'Null'}</>,
      [TEST_DEFINITION_NAMES_COLUMN.accessorKey]: (
        <ChipList
          itemIds={testDefinitionIds}
          chipSize={ChipSize.TABLE_CELL}
          isVertical={false}
          maxChips={4}
          showChipsAsLinks={false}
        />
      ),
    },
  };
}

function getRowContentHeight(item: SourceItemWithTestOrderInfos): number {
  const contentsCellHeight =
    item.__typename === 'ContainerNode'
      ? getChipsRowHeight(Math.min(3, item.contents.length), ChipSize.TABLE_CELL) // 2 chips + 1 expand button
      : TABLE_ROW_BODY_HEIGHT;

  return contentsCellHeight;
}

export interface ChooseTestDilutionsForSourcesModalProps<TSourceItem extends SourceItemWithTestOrderInfos> {
  sourceItems: Array<TSourceItem>;
  onRequestClose: () => void;
  onRequestSubmit: (selectedItems: Array<TSourceItem>) => void;
  procedureTestOrderConfig: ProcedureTestOrderConfig;
  plateMapType: PlateMapType;
}

/**
 * This modal lets the user narrow down which valid (source item, test dilution) combinations to use.
 *
 * The UI only lets you select from source items that have test orders. Source items WITHOUT test orders are
 * silently passed through to onRequestSubmit because test dilutions are not applicable to them.
 *
 * Calls onRequestSubmit with all of the following:
 *
 *    the chosen (source item, dilution) combinations:
 *      - if some but not all dilutions are selected, the item is included, but its test orders are narrowed
 *        down to the selected dilutions
 *      - if no dilutions are selected for the source item, the item is not included
 *
 *    and include the source items that do not have test orders.
 *
 */
export default function ChooseTestDilutionsForSourcesModal<TSourceItem extends SourceItemWithTestOrderInfos>({
  sourceItems,
  onRequestClose,
  onRequestSubmit,
  procedureTestOrderConfig,
  plateMapType,
}: ChooseTestDilutionsForSourcesModalProps<TSourceItem>): JSX.Element {
  const [testOrderedItems, itemsWithoutTestOrders] = useMemo(
    () => partition(sourceItems, (item) => getTestOrders(item).length > 0),
    [sourceItems]
  );
  // Prevent invalid test orders from being selectable or even visible
  const itemsWithFilteredTestOrders = useMemo(
    () =>
      testOrderedItems.map((item): TSourceItem => {
        const error = validateSourceTestOrders({item, procedureTestOrderConfig, plateMapType});
        assert(error == null, 'This modal should only receive valid sources', {error});
        const validTestOrders = getTestOrders(item).filter((to) => {
          const invalidMsg = validateTestOrder(to, procedureTestOrderConfig);
          return invalidMsg == null;
        });
        assert(validTestOrders.length > 0, 'Valid items should have at least one valid test order');
        return produce(item, (draft) => {
          mutateItemToReplaceTestOrders(draft, validTestOrders);
        });
      }),
    [testOrderedItems, procedureTestOrderConfig, plateMapType]
  );
  const selectableSourceDilutionKeys: Set<string> = useMemo(
    () =>
      new Set(
        itemsWithFilteredTestOrders.flatMap((item) =>
          getTestOrders(item).map((to) =>
            getSourceDilutionKey({sourceId: item.id, dilutionFactor: to.dilutionFactor})
          )
        )
      ),
    [itemsWithFilteredTestOrders]
  );

  const [selectedSourceDilutionKeys, setSelectedSourceDilutionKeys] = useState<Set<string>>(
    selectableSourceDilutionKeys
  );

  const itemsById = groupByUniqueKey(itemsWithFilteredTestOrders, (item) => item.id);
  const columns = [
    SOURCE_ITEM_COLUMN,
    ...(itemsWithFilteredTestOrders.some(({__typename}) => __typename === 'ContainerNode')
      ? [CONTENTS_COLUMN]
      : []),
    TEST_DILUTION_FACTOR_COLUMN,
    TEST_DEFINITION_NAMES_COLUMN,
  ];
  const rows: Array<ChooseTestDilutionsForSourcesRow> = [...selectableSourceDilutionKeys.values()].map(
    (sourceDilutionKey) => makeTableRow(sourceDilutionKey, selectedSourceDilutionKeys, itemsById)
  );

  const onSelectRows = (
    affectedRows: Array<ChooseTestDilutionsForSourcesRow>,
    newIsSelected: boolean
  ): void => {
    const affectedSourceDilutionKeys = new Set(affectedRows.map(({id}) => id));
    const newSelectedSourceDilutionKeys = newIsSelected
      ? new Set([...selectedSourceDilutionKeys, ...affectedSourceDilutionKeys])
      : new Set([...selectedSourceDilutionKeys].filter((key) => !affectedSourceDilutionKeys.has(key)));
    setSelectedSourceDilutionKeys(newSelectedSourceDilutionKeys);
  };

  const onSubmit = (): void => {
    const selectedDilutionFactorsBySourceId: Record<string, Array<number | null>> = mapObject(
      groupBy(
        [...selectedSourceDilutionKeys],
        (sourceDilutionKey) => parseSourceDilutionFromKey(sourceDilutionKey).sourceId
      ),
      (sourceDilutionKeys) =>
        sourceDilutionKeys.map(
          (sourceDilutionKey) => parseSourceDilutionFromKey(sourceDilutionKey).dilutionFactor
        )
    );
    const selectedItemsWithSelectedTestOrders = itemsWithFilteredTestOrders
      .filter(({id}) => selectedDilutionFactorsBySourceId.hasOwnProperty(id))
      .map((item) =>
        produce(item, (draft) => {
          const selectedDilutionFactors = selectedDilutionFactorsBySourceId[item.id];
          const testOrders = getTestOrders(item);
          const selectedTestOrders = testOrders.filter((to) =>
            selectedDilutionFactors.includes(to.dilutionFactor)
          );
          mutateItemToReplaceTestOrders(draft, selectedTestOrders);
        })
      );
    const finalSourceItemsById = groupByUniqueKey(
      [...selectedItemsWithSelectedTestOrders, ...itemsWithoutTestOrders],
      ({id}) => id
    );
    // Preserve order from sourceItems
    onRequestSubmit(sourceItems.map(({id}) => finalSourceItemsById[id]).filter((item) => item != null));
  };

  return (
    <Modal
      title="Select samples to test"
      onRequestClose={onRequestClose}
      buttons={[
        {text: 'Cancel', cancel: true},
        {
          text: `Add sources`,
          style: ButtonStyle.PRIMARY,
          submit: true,
          disabled: sourceItems.length === 0,
        },
      ]}
      onRequestSubmit={onSubmit}
      fullWidth={true}
      dataTestAttrs={{component: 'ChooseTestDilutionsForSourcesModal', element: 'root'}}
      expandHeight={true}
    >
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text="Select the desired dilution for each trial sample with open test orders"
        supportingInfo="Sources selected in the previous step without test dilutions are not shown here"
      />
      <p className={css(styles.selectionCount)}>{selectedSourceDilutionKeys.size} selected</p>
      <SortableScrollableTable
        columns={columns}
        initialRows={rows}
        onRowsSelect={onSelectRows}
        rowSelectionMode={RowSelectionMode.MULTIPLE}
        canResizeColumns={true}
        getSortValue={(row, sortKey) => {
          switch (sortKey) {
            case TEST_DILUTION_FACTOR_COLUMN.sortKey:
              return row.data.dilutionFactor;
            case TEST_DEFINITION_NAMES_COLUMN.sortKey:
              // Test definition ids are sorted to guarantee stable order
              return row.data.testDefinitionIds.join(',');
            default:
              // Original order
              return rows.findIndex(({id}) => id === row.id);
          }
        }}
        initialSort={[]}
        tableKey="choose-test-dilutions-table"
        getRowContentHeight={(i) => {
          const key = rows[i]?.id;
          return key == null // no rows
            ? TABLE_ROW_BODY_HEIGHT
            : getRowContentHeight(itemsById[parseSourceDilutionFromKey(key).sourceId]);
        }}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  selectionCount: {
    ...FONT_PROXIMA_13,
    marginTop: '10px',
    color: GRAY_DARKER,
  },
});
